import { useId } from 'react'
import { MessageBar, MessageBarType, Spinner, SpinnerSize, Stack, Text } from '@fluentui/react'
import { QuarterlySubscribersResponse } from '../../api/models'
import KrProgressBar from './KrProgressBar'
import KrStatusBadge from './KrStatusBadge'
import PastQuartersTable from './PastQuartersTable'
import Disclosure from './Disclosure'
import ExcludedUsersNote from './ExcludedUsersNote'
import SubscribersTable from './SubscribersTable'
import { formatQuarterDates, krStatusOf, KrStatus } from './krFormat'
import { useQuarterlySubscribers } from './useQuarterlySubscribers'
import { MUTED_TEXT } from './krStyles'

const PROGRESS_COLORS: Record<KrStatus, string> = {
  met: '#5B8A72',
  'not-met': '#B8583D',
  'in-progress': '#496A99',
}

const KrSummary = ({ data }: { data: QuarterlySubscribersResponse }) => {
  const { current, target, history } = data
  const status = krStatusOf(current)
  const remaining = Math.max(0, target - current.subscriber_count)

  return (
    <Stack tokens={{ childrenGap: 12 }}>
      <Text variant="small" styles={MUTED_TEXT}>
        {current.label} · {formatQuarterDates(current)}
      </Text>
      <Stack horizontal verticalAlign="center" wrap tokens={{ childrenGap: 12 }}>
        <Text variant="xxLarge" styles={{ root: { fontWeight: 700, color: '#2B3A67' } }}>
          {current.subscriber_count.toLocaleString()} / {target.toLocaleString()} subscribers
        </Text>
        <KrStatusBadge status={status} />
        {status === 'in-progress' && (
          <Text variant="small" styles={MUTED_TEXT}>
            {remaining.toLocaleString()} to go
          </Text>
        )}
      </Stack>
      <KrProgressBar count={current.subscriber_count} target={target} color={PROGRESS_COLORS[status]} />
      <Text variant="small" styles={MUTED_TEXT}>
        Distinct non-CEM users who used Query this quarter. CEM staff and demo accounts are excluded.
      </Text>
      <ExcludedUsersNote quarter={current} />
      <Disclosure label={`Subscribers this quarter (${current.subscriber_count.toLocaleString()})`}>
        <SubscribersTable
          subscribers={current.subscribers}
          ariaLabel={`Subscribers in ${current.label}`}
          emptyMessage="No subscribers yet this quarter."
        />
      </Disclosure>
      <PastQuartersTable history={history} />
    </Stack>
  )
}

const KrTracker = () => {
  const kr = useQuarterlySubscribers()
  const headingId = useId()

  return (
    <Stack
      as="section"
      aria-labelledby={headingId}
      aria-busy={kr.status === 'loading'}
      styles={{
        root: {
          padding: '24px',
          backgroundColor: '#fff',
          borderRadius: 8,
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          borderTop: '3px solid #2B3A67',
        },
      }}
      tokens={{ childrenGap: 8 }}
    >
      <Text id={headingId} as="h2" variant="mediumPlus" styles={{ root: { margin: 0, fontWeight: 600, color: '#605e5c' } }}>
        Quarterly subscriber KR
      </Text>
      {kr.status === 'loading' && (
        <Spinner size={SpinnerSize.small} label="Loading KR..." labelPosition="right" styles={{ root: { justifyContent: 'flex-start' } }} />
      )}
      {kr.status === 'error' && <MessageBar messageBarType={MessageBarType.error}>{kr.message}</MessageBar>}
      {kr.status === 'loaded' && <KrSummary data={kr.data} />}
    </Stack>
  )
}

export default KrTracker
